import { JSX, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDialog } from '../../../components/Dialogs/dialog-base'
import { PackagingOption } from '../useOrderConfirmationEditViewModel'
import { useProfileContext } from '../../../../contexts/ProfileContext'
import { OrderDialog } from './OrderDialog'
import { Packaging } from '../../../../core/models/entities/Order'

interface Props {
  component: JSX.Element
  open: () => void
}

export function useDeliveryPackagingOrderDialog({
  onContinue,
  onCancel,
  packaging,
  currency: orderCurrency
}: { onContinue: () => Promise<void>, onCancel?: () => void, packaging: PackagingOption, currency?: string }): Props {

  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [toggle, setToggle] = useState<boolean>(false);

  const dialog = useDialog()
  const { profile, currency: profileCurrency } = useProfileContext()
  // Label with the ORDER's currency (row-derived for the local draft, the
  // order's own for a fetched one — #603) so the dialog agrees with every
  // other amount on the confirm screen; profile currency is the fallback
  // only. The packaging fee itself is added server-side in the order org's
  // context, so the order currency is what the fee will actually carry.
  const currency = orderCurrency || profileCurrency

  function handleSubmit() {
    setLoading(true);
    onContinue().finally(() => {
      setLoading(false)
      setToggle(false);
      dialog.closeDialog()
    })
  }

  function handleToggle() {
    setToggle(!toggle)
  }

  function handleCancel() {
    if (onCancel) onCancel()
    dialog.closeDialog()
  }

  const getPackagingTypeText = () => {
    switch (packaging.type) {
      case Packaging.NoPackaging: return t("orderConfirmation.packagingTextNone")
      case Packaging.StandardBagPackaging: return t("orderConfirmation.packagingTextStandardBag")
      case Packaging.SpecificPackaging: return t("orderConfirmation.packagingTextSpecific")
      default: return t("orderConfirmation.packagingTextUnknown")
    }
  }

  // The profile-configured terms (orderOptionsTermsOf*Packaging) are stored in
  // the DB in English only and can't follow the UI language, so the dialog
  // body is rendered from translation keys instead.
  const getPackagingTermsText = () => {
    switch (packaging.type) {
      case Packaging.NoPackaging: return ""
      case Packaging.StandardBagPackaging: return t("orderConfirmation.packagingTermsStandardBag")
      case Packaging.SpecificPackaging: return t("orderConfirmation.packagingTermsSpecific")
      default: return ""
    }
  }

  const getPackagingAmountValue = () => {
    switch (packaging.type) {
      case Packaging.NoPackaging: return 0
      case Packaging.StandardBagPackaging: return profile!.orderOptions.orderOptionsStandardPackagingCost
      case Packaging.SpecificPackaging: return profile!.orderOptions.orderOptionsSpecificPackagingCost
      default: return 0
    }
  }

  return {
    component: <>
      <OrderDialog
        dialogVm={dialog}
        onToggle={handleToggle}
        onSubmit={handleSubmit}
        loading={loading}
        onCancel={handleCancel}
        toggle={toggle}
        title={packaging.title}
        termsText={getPackagingTermsText()}
        explanation={t("orderConfirmation.packagingFeeExplanation", { packaging: getPackagingTypeText() })}
        boldPrice={`${currency} ${getPackagingAmountValue()} ${packaging.feeUnit}`}
      />
    </>,
    open: dialog.openDialog
  }
}
